import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePWABanner } from "@/hooks/usePWABanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { authApi } from "@/services/authService";
import { useMutation } from "@tanstack/react-query";
import PWAInstallBanner from "@/components/mobile/PWAInstallBanner";
import { cn } from "@/lib/utils";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isBannerVisible } = usePWABanner();

  const { mutate: forgetPassword, isPending: isSubmitting } = useMutation({
    mutationFn: ({ email }: { email: string }) => authApi.forgetPassword(email),

    onSuccess: () => {
      toast({
        title: "Reset link sent",
        description:
          "A password reset link has been sent to your email address.",
      });
      setEmailError("");
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      console.error("Forgot Password error:", err);
      
      // Handle 404 - account not found
      if (err?.response?.status === 404) {
        setEmailError(err?.response?.data?.error || "No account found with this email address.");
        return;
      }
      
      // Handle other errors with toast
      toast({
        title: "Password reset failed",
        description:
          err?.response?.data?.error ||
          "An unexpected error occurred while trying to reset your password.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(""); // Clear any previous errors
    forgetPassword({ email });
  };

  return (
    <>
      {/* PWA Install Banner */}
      <PWAInstallBanner />
      
      <div className={cn(
        "min-h-screen flex items-center justify-center bg-gray-50 p-4",
        isBannerVisible ? "pt-20" : "pt-4"
      )}>
        <div className="w-full max-w-md">
          <div className="mb-5 text-center">
            <div className="flex items-center justify-center">
              <img src="/logo.svg" alt="Quantum Warehouse Solutions logo" />
            </div>
            <p className="mt-2 text-gray-600">Reset your password</p>
          </div>
        <Card>
          <CardHeader>
            <CardTitle>Forgot Password</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your
              password.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(""); // Clear error when user types
                  }}
                  required
                  className={emailError ? "border-red-500" : ""}
                />
                {emailError && (
                  <p className="text-sm text-red-500 mt-1">{emailError}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending Reset Link..." : "Send Reset Link"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
    </>
  );
};

export default ForgotPassword;
